/**
 * Runtime loader for the standalone "download cruise PDF" bundle
 * (`DownloadPDFWrapper.js` + `DownloadPDFWrapper.css`), built independently
 * by `apps/my-msc/vite.downloadPdfChunk.config.ts` (see that file and
 * `further-optimizations.md` "CRITICAL FIX" for why this can't be a plain
 * `React.lazy(() => import(...))` in this repo's build pipeline).
 *
 * The JS chunk is loaded via a classic `<script>` tag (not `type="module"`,
 * since it's a native Rollup `iife` bundle) sitting alongside the main
 * app's own emitted files. It sets `globalThis.__mscDownloadPDFWrapperModule`
 * itself once executed; this module never imports it via ESM. The CSS
 * chunk (this component's own CSS-module styles, plus anything pulled in
 * from its dependency graph, e.g. shared-components' Button/Image/Text) is
 * loaded via an injected `<link rel="stylesheet">` -- it is NOT part of the
 * main app's `style.css` (deliberately excluded, since the whole point is
 * to defer it), so without this it would render unstyled.
 */
import type DownloadPDFWrapperType from './components/DownloadPDFWrapper';

const CHUNK_FILE_NAME = 'DownloadPDFWrapper.js';
const CHUNK_CSS_FILE_NAME = 'DownloadPDFWrapper.css';

// Captured once, synchronously, at module top level -- this is the exact
// technique `rollup-plugin-iife` itself relies on internally for
// `import.meta.url`, so it survives this repo's main-app iife rewrite
// unchanged and reliably resolves to this script's own deployed base URL.
const CURRENT_SCRIPT_URL =
  (typeof document !== 'undefined' &&
    document.currentScript &&
    (document.currentScript as HTMLScriptElement).src) ||
  (typeof globalThis !== 'undefined' && globalThis.location && globalThis.location.href) ||
  '';

let loadPromise: Promise<typeof DownloadPDFWrapperType> | null = null;

function resolveUrl(fileName: string): string {
  try {
    return new URL(fileName, CURRENT_SCRIPT_URL).href;
  } catch {
    return fileName;
  }
}

function injectStylesheet(href: string): void {
  if (document.querySelector(`link[data-msc-chunk-css="${CHUNK_CSS_FILE_NAME}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.mscChunkCss = CHUNK_CSS_FILE_NAME;
  document.head.appendChild(link);
}

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-msc-chunk="${CHUNK_FILE_NAME}"]`
    );
    if (existing) {
      if (globalThis.__mscDownloadPDFWrapperModule) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.mscChunk = CHUNK_FILE_NAME;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
    document.head.appendChild(script);
  });
}

/**
 * Loads (once) and resolves the DownloadPDFWrapper component. Safe to call
 * multiple times/from multiple component instances -- the underlying
 * `<script>`/`<link>` injection and network fetch only ever happen once.
 */
export function loadDownloadPdfWrapper(): Promise<typeof DownloadPDFWrapperType> {
  if (globalThis.__mscDownloadPDFWrapperModule) {
    return Promise.resolve(globalThis.__mscDownloadPDFWrapperModule);
  }

  if (!loadPromise) {
    injectStylesheet(resolveUrl(CHUNK_CSS_FILE_NAME));
    loadPromise = injectScript(resolveUrl(CHUNK_FILE_NAME)).then(() => {
      if (!globalThis.__mscDownloadPDFWrapperModule) {
        throw new Error(
          `${CHUNK_FILE_NAME} loaded but did not define globalThis.__mscDownloadPDFWrapperModule`
        );
      }
      return globalThis.__mscDownloadPDFWrapperModule;
    });
  }

  return loadPromise;
}

declare global {
  // eslint-disable-next-line no-var
  var __mscDownloadPDFWrapperModule: typeof DownloadPDFWrapperType | undefined;
}
